import {
  Button,
  Flex,
  Paper,
  TextInput,
  Text,
  Loader,
  NumberInput,
  Checkbox,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  displayErrorMessage,
  displayServerErrorMessage,
} from "../shared/ErrorMessage";
import {
  UpsertAssignmentRequest,
  useUpsertAssignmentMutation,
} from "../slices/api/assignmentsApi";
import { initialize, updateField } from "../slices/form/upsertAssignment";
import { useEffect, useRef } from "react";

interface Props {
  title: string;
  ventId: string;
  guestId: string;
  activityId: string;
  assignmentQuantity: number;
  assignmentComment: string;
  limitPerGuest: number;
  limitPerEvent: number;
  othersAssignmentQuantity: number;
  onClose: () => void;
}
const UpsertAssignment = (props: Props) => {
  const { data: assignment } = useAppSelector(
    (state: RootState) => state.upsertAssignmentForm
  );
  const { ventId, guestId, activityId } = props;
  const [upsertAssignment] = useUpsertAssignmentMutation();
  const nameRefQuantity = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(
      initialize({
        quantity: props.assignmentQuantity,
        comment: props.assignmentComment,
      })
    );
    if (props.assignmentQuantity === 0) {
      dispatch(
        updateField({
          field: "quantity",
          value: 1, // Default to 1 when creating a new assignment -- quicker for user
        })
      );
    }
  }, []);

  const handleSave = async () => {
    if (assignment === undefined) {
      console.error("Assignment is undefined but should not be");
      return;
    }

    if (assignment.quantity > props.limitPerGuest) {
      displayErrorMessage(`The limit per guest is ${props.limitPerGuest}.`);
      return;
    }

    if (assignment.comment?.length > 50) {
      displayErrorMessage("Comment cannot be longer than 50 characters.");
      return;
    }

    if (assignment.quantity === 0 && assignment.comment !== "") {
      displayErrorMessage(
        "You are about to remove the assignment, but there's a comment. If you really want to remove the assignment, then erase the comment."
      );
      return;
    }

    const request: UpsertAssignmentRequest = {
      ventId,
      assignment,
      guestId,
      activityId,
    };

    try {
      await upsertAssignment(request).unwrap();
      props.onClose();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (!assignment) {
    return <Loader color="blue" />;
  }

  // Quantity could be > 1 when the limitPerGuest is current 1 -- could have been changed after the quantity was set
  const showCheckbox = props.limitPerGuest === 1 && assignment.quantity <= 1;

  return (
    <>
      <Paper>
        <Text size="lg">{props.title}</Text>
        {showCheckbox === false && (
          <NumberInput
            required
            min={0}
            maxLength={6}
            ref={nameRefQuantity}
            mt="md"
            allowNegative={false}
            allowDecimal={false}
            label={
              `Quantity: max ${props.limitPerGuest} per guest` +
              (props.limitPerEvent === 0
                ? ""
                : `, Remaining ${props.limitPerEvent - props.othersAssignmentQuantity - assignment.quantity}`)
            }
            value={assignment.quantity}
            onChange={(value) => {
              dispatch(
                updateField({
                  field: "quantity",
                  value: value === "" ? 0 : value,
                })
              );
            }}
            onFocus={() => {
              if (nameRefQuantity.current) {
                nameRefQuantity.current.focus(); // Ensure focus is set before selection
                nameRefQuantity.current.select();
              }
            }}
          />
        )}
        {showCheckbox && (
          <Checkbox
            mt="md"
            label={`Assign activity`}
            checked={assignment.quantity > 0}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "quantity",
                  value: event.currentTarget.checked ? 1 : 0,
                })
              )
            }
          />
        )}
        <TextInput
          mt="md"
          label="Comment"
          value={assignment.comment ?? ""}
          maxLength={50}
          onChange={(e) => {
            dispatch(
              updateField({
                field: "comment",
                value: e.currentTarget.value,
              })
            );
          }}
        />
        <Flex justify="flex-end">
          <Button mt="md" mr="md" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            mt="md"
            onClick={handleSave}
            disabled={
              props.assignmentQuantity === assignment.quantity &&
              props.assignmentComment === assignment.comment
            }
          >
            Save
          </Button>
        </Flex>
      </Paper>
    </>
  );
};

export default UpsertAssignment;
